import React from 'react';
import { Button, Checkbox, InputNumber, Radio, Space, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { IconPicker, IconThumb, useUploadedIcons } from '../../modules/iconLibrary';

/**
 * 叠加图层 · 图层设置
 * （原「地图配置 → 地图设置」中的叠加点击飞行 / 全景挂点）
 */
export default function LayerSettingsPanel({
  mapSettings,
  setMapSettings,
  handleSaveMapSettings,
  mapSettingsMsg
}) {
  const { icons, loading: iconsLoading, uploadIcon, reload } = useUploadedIcons();

  const handleUpload = async (file) => {
    try {
      const icon = await uploadIcon(file, { description: '全景地图挂点' });
      message.success('已加入图标库');
      if (icon?.id) {
        setMapSettings((prev) => ({ ...prev, panorama_marker_icon_id: icon.id }));
      }
    } catch (e) {
      message.error(e.message || '上传失败');
    }
    return false;
  };

  const selectedIcon = icons.find((i) => Number(i.id) === Number(mapSettings.panorama_marker_icon_id));
  const previewSize = Math.max(12, Math.min(48, Number(mapSettings.panorama_marker_size) || 22));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleSaveMapSettings}>保存设置</Button>
        {mapSettingsMsg && (
          <span style={{
            marginLeft: 12,
            color: mapSettingsMsg.includes('成功') ? 'green' : 'red'
          }}
          >
            {mapSettingsMsg}
          </span>
        )}
      </div>

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 8 }}>地图叠加 · 点击成品名称时</h4>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>
          指挥台「路网 / 等高线」竖型菜单中，点击名称时的飞行动作（不切换勾选）。
        </div>
        <Radio.Group
          value={mapSettings.overlay_name_click_fly === 'device' ? 'device' : 'bounds'}
          onChange={(e) => setMapSettings((prev) => ({
            ...prev,
            overlay_name_click_fly: e.target.value
          }))}
          options={[
            { value: 'bounds', label: '飞到图层范围（如整市等高线/路网范围）' },
            { value: 'device', label: '飞到最近活跃设备位置' }
          ]}
        />
      </div>

      <div>
        <h4 style={{ marginBottom: 8 }}>全景影像 · 地图挂点</h4>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>
          已发布且有经纬度的全景场景，在指挥台用地图图标标注。自定义图标请到「系统设置 → 地图配置 → 图标库管理」上传后在此选择；尺寸建议小于队员坐标点（约 28px）。
        </div>
        <Checkbox
          checked={mapSettings.panorama_show_map_markers !== false}
          onChange={(e) => setMapSettings((prev) => ({
            ...prev,
            panorama_show_map_markers: e.target.checked
          }))}
        >
          在地图上标注有坐标的全景挂点
        </Checkbox>
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div>
            <div style={{ marginBottom: 6, fontSize: 13 }}>图标</div>
            <Space wrap>
              <IconPicker
                source="uploaded"
                valueMode="id"
                allowClear
                showPreview={false}
                placeholder="默认全景图标（蓝圆）"
                value={mapSettings.panorama_marker_icon_id || undefined}
                onChange={(v) => setMapSettings((prev) => ({
                  ...prev,
                  panorama_marker_icon_id: v == null ? null : v
                }))}
              />
              <Upload
                accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
                showUploadList={false}
                beforeUpload={handleUpload}
              >
                <Button icon={<UploadOutlined />} loading={iconsLoading}>上传到图标库</Button>
              </Upload>
              <Button type="link" size="small" onClick={() => reload()}>刷新</Button>
            </Space>
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 13 }}>图标大小（像素）</div>
            <InputNumber
              min={12}
              max={48}
              value={mapSettings.panorama_marker_size ?? 22}
              onChange={(v) => setMapSettings((prev) => ({
                ...prev,
                panorama_marker_size: v
              }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 13 }}>预览</div>
            <div style={{
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#0f172a',
              borderRadius: 8
            }}
            >
              {selectedIcon?.file_path || selectedIcon?.url ? (
                <IconThumb
                  iconId={selectedIcon.id}
                  url={selectedIcon.file_path || selectedIcon.url}
                  size={previewSize}
                />
              ) : (
                <span
                  style={{ display: 'inline-block', width: previewSize, height: previewSize }}
                  dangerouslySetInnerHTML={{
                    __html: `<svg width="${previewSize}" height="${previewSize}" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg"><circle cx="17" cy="17" r="15" fill="#1d4ed8" stroke="#fff" stroke-width="2.5"/><path d="M8 17a9 9 0 0 1 18 0" fill="none" stroke="#93c5fd" stroke-width="1.8"/><path d="M11 17a6 6 0 0 1 12 0" fill="none" stroke="#bfdbfe" stroke-width="1.5"/><circle cx="17" cy="17" r="2.6" fill="#fff"/></svg>`
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
